/**
 * Column definitions for the V2 Fleet List (Hybrid Search API).
 *
 * Only fields returned by POST /api/home/search/hybrid are included.
 * Snake_case API fields are normalised to camelCase by useHybridSearch.
 */

// Country name/code → flag emoji mapping (shared with V1)
const COUNTRY_FLAGS = {
  "united states": "\u{1F1FA}\u{1F1F8}",
  us: "\u{1F1FA}\u{1F1F8}",
  usa: "\u{1F1FA}\u{1F1F8}",
  "united kingdom": "\u{1F1EC}\u{1F1E7}",
  uk: "\u{1F1EC}\u{1F1E7}",
  gb: "\u{1F1EC}\u{1F1E7}",
  germany: "\u{1F1E9}\u{1F1EA}",
  de: "\u{1F1E9}\u{1F1EA}",
  france: "\u{1F1EB}\u{1F1F7}",
  fr: "\u{1F1EB}\u{1F1F7}",
  india: "\u{1F1EE}\u{1F1F3}",
  in: "\u{1F1EE}\u{1F1F3}",
  australia: "\u{1F1E6}\u{1F1FA}",
  au: "\u{1F1E6}\u{1F1FA}",
  aus: "\u{1F1E6}\u{1F1FA}",
  canada: "\u{1F1E8}\u{1F1E6}",
  ca: "\u{1F1E8}\u{1F1E6}",
  japan: "\u{1F1EF}\u{1F1F5}",
  jp: "\u{1F1EF}\u{1F1F5}",
  china: "\u{1F1E8}\u{1F1F3}",
  cn: "\u{1F1E8}\u{1F1F3}",
  brazil: "\u{1F1E7}\u{1F1F7}",
  br: "\u{1F1E7}\u{1F1F7}",
  netherlands: "\u{1F1F3}\u{1F1F1}",
  nl: "\u{1F1F3}\u{1F1F1}",
  nld: "\u{1F1F3}\u{1F1F1}",
  italy: "\u{1F1EE}\u{1F1F9}",
  it: "\u{1F1EE}\u{1F1F9}",
  spain: "\u{1F1EA}\u{1F1F8}",
  es: "\u{1F1EA}\u{1F1F8}",
  "south korea": "\u{1F1F0}\u{1F1F7}",
  kr: "\u{1F1F0}\u{1F1F7}",
  mexico: "\u{1F1F2}\u{1F1FD}",
  mx: "\u{1F1F2}\u{1F1FD}",
  sweden: "\u{1F1F8}\u{1F1EA}",
  se: "\u{1F1F8}\u{1F1EA}",
  norway: "\u{1F1F3}\u{1F1F4}",
  no: "\u{1F1F3}\u{1F1F4}",
  denmark: "\u{1F1E9}\u{1F1F0}",
  dk: "\u{1F1E9}\u{1F1F0}",
  finland: "\u{1F1EB}\u{1F1EE}",
  fi: "\u{1F1EB}\u{1F1EE}",
  switzerland: "\u{1F1E8}\u{1F1ED}",
  ch: "\u{1F1E8}\u{1F1ED}",
  belgium: "\u{1F1E7}\u{1F1EA}",
  be: "\u{1F1E7}\u{1F1EA}",
  bel: "\u{1F1E7}\u{1F1EA}",
  austria: "\u{1F1E6}\u{1F1F9}",
  at: "\u{1F1E6}\u{1F1F9}",
  portugal: "\u{1F1F5}\u{1F1F9}",
  pt: "\u{1F1F5}\u{1F1F9}",
  ireland: "\u{1F1EE}\u{1F1EA}",
  ie: "\u{1F1EE}\u{1F1EA}",
  "new zealand": "\u{1F1F3}\u{1F1FF}",
  nz: "\u{1F1F3}\u{1F1FF}",
  singapore: "\u{1F1F8}\u{1F1EC}",
  sg: "\u{1F1F8}\u{1F1EC}",
};

const getCountryFlag = country => {
  if (!country) return "\u{1F30D}";
  return COUNTRY_FLAGS[country.toLowerCase().trim()] || "\u{1F30D}";
};

/**
 * Format epoch timestamp (milliseconds) or ISO string to relative time.
 * Returns "—" if no value.
 */
const formatTimestamp = value => {
  if (!value) return "\u2014";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "\u2014";

  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
};

export const getColumnsV2 = (onSerialNumberClick = () => {}) => [
  {
    label: "Serial Number",
    key: "serialNumber",
    filterable: true,
    render: (value, row) => {
      const { siteId } = row;
      return (
        <button className="serial-link" onClick={() => onSerialNumberClick(value, siteId)}>
          {value}
        </button>
      );
    },
  },
  { label: "Site ID", key: "siteId" },
  { label: "Display Name", key: "displayName" },
  { label: "Vendor Name", key: "vendorName" },
  { label: "Model Name", key: "modelName" },
  { label: "Protocol", key: "protocol" },
  {
    label: "Firmware Version",
    key: "fwVersion",
    render: value => (value ? <span className="fw-chip">{value}</span> : "\u2014"),
  },
  {
    label: "Country",
    key: "country",
    render: value => {
      if (!value) return "\u2014";
      return (
        <span className="country-cell">
          <span className="country-cell__flag">{getCountryFlag(value)}</span>
          <span>{value}</span>
        </span>
      );
    },
  },
  { label: "City", key: "city" },
  { label: "State", key: "state" },
  { label: "Owner", key: "owner" },
  {
    label: "Connection Status",
    key: "isConnected",
    render: value => {
      if (value === null || value === undefined) return "\u2014";
      const connected = Boolean(value);
      return (
        <span className={`status-badge status-badge--${connected ? "connected" : "disconnected"}`}>
          <span className={`status-dot status-dot--${connected ? "connected" : "disconnected"}`} />
          {connected ? "Connected" : "Disconnected"}
        </span>
      );
    },
  },
  {
    label: "Session State",
    key: "sessionState",
    render: value => {
      if (!value) return "\u2014";
      const colorMap = {
        STARTED: "connected",
        TX_STARTED: "connected",
        CREATED: "connected",
        ENDED: "disconnected",
        STOPPED: "disconnected",
      };
      const cls = colorMap[value] || "disconnected";
      return <span className={`status-badge status-badge--${cls}`}>{value}</span>;
    },
  },
  {
    label: "Plugged In",
    key: "connectorIsPluggedIn",
    render: value => {
      if (value === null || value === undefined) return "\u2014";
      return value ? "Yes" : "No";
    },
  },
  { label: "Phase Count", key: "phaseCount", filterable: true },
  { label: "Max Current (A)", key: "maxCurrent", filterable: true },
  { label: "Breaker Current (A)", key: "breakerCurrent", filterable: true },
  { label: "Operating Voltage (V)", key: "operatingVoltage", filterable: true },
  {
    label: "Last Reported At",
    key: "lastReportedAt",
    render: value => {
      const text = formatTimestamp(value);
      if (text === "\u2014") return text;
      const date = new Date(value);
      return (
        <span className="relative-time" title={date.toISOString()}>
          {text}
        </span>
      );
    },
  },
  {
    label: "Created At",
    key: "createdAt",
    render: value => {
      if (!value) return "\u2014";
      return new Date(value).toLocaleDateString();
    },
  },
  {
    label: "Updated At",
    key: "updatedAt",
    render: value => {
      if (!value) return "\u2014";
      return new Date(value).toLocaleDateString();
    },
  },
  { label: "Connector Status", key: "connectorStatusReason" },
  {
    label: "Gateway Status",
    key: "gatewayConnectivityStatus",
    render: value => {
      if (!value && value !== 0) return "\u2014";
      const label = value === 0 || value === "OFFLINE" ? "OFFLINE" : String(value);
      const isOnline = label === "ONLINE";
      return <span className={`status-badge status-badge--${isOnline ? "connected" : "disconnected"}`}>{label}</span>;
    },
  },
  {
    label: "Retired",
    key: "isRetired",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Faulted",
    key: "isFaulted",
    render: value => {
      if (value === null || value === undefined) return "\u2014";
      return value ? <span className="status-badge status-badge--disconnected">Yes</span> : "No";
    },
  },
];
